/**
 * Le planning des ressources — la fenêtre qu'on ouvre à côté du plan de charge.
 *
 * *Demande de Chris, 03/09/2026 : « le responsable exploitation va aller dans tableau de bord,
 * exploitation et plan de charge pour affecter les ressources réelles en face des génériques ; je
 * voudrais que quand il est dans cette page d'affectation il puisse ouvrir une fenêtre
 * supplémentaire qui contient sous forme de calendrier le planning de toutes les ressources main
 * d'œuvre et un deuxième planning de toutes les ressources matériel […] voir jour par jour et
 * demi-journée par demi-journée les heures déjà affectées sur les ressources, en plus les congés
 * et les maladies pour la main d'œuvre, et pour les machines les pannes et les heures déjà
 * affectées sur d'autres chantiers […] borner avec un sélecteur de date […] directement filtrées
 * sur les ressources de l'APO afin de recibler les informations pertinentes ; par contre s'il
 * veut afficher toutes les ressources il doit pouvoir aussi le faire. »*
 *
 * ## Une vraie seconde fenêtre, et non un panneau par-dessus
 *
 * *« Simultanément à sa vue du plan de charge »* : une boîte modale recouvrirait justement ce
 * qu'on veut garder sous les yeux. C'est donc une fenêtre du navigateur, ouverte sur son propre
 * chemin, qu'on pose à côté ou sur le second écran. Elle porte un **nom fixe par vue** :
 * recliquer avec une autre affaire rafraîchit la fenêtre au lieu d'en empiler une troisième.
 *
 * ## Deux vues, et la même case décide des deux
 *
 * *Demande de Chris, 03/09/2026 : « pour organiser le chantier, le responsable transport […] il
 * faut avoir le même principe que ce que nous venons de faire pour l'exploitation, mais les
 * plannings des ressources doivent afficher uniquement les ressources main d'œuvre transport puis
 * les ressources matériel transport, avec également les congés maladie etc. Dans les ressources,
 * pas la peine d'afficher les matériels transport −3,5 t puisqu'ils sont affichés côté
 * exploitation. »*
 *
 * La vue voyage dans l'adresse : c'est l'onglet d'où l'on vient qui la décide. Et les deux côtés
 * sont **exactement complémentaires**, parce qu'une seule case du référentiel les sépare — les
 * `VU`, `VUL` et `VP` ne la portent pas, donc les véhicules de moins de 3,5 t restent avec
 * l'exploitation sans qu'aucune règle supplémentaire ne soit écrite ici. Aucune ressource ne peut
 * manquer aux deux vues, ni figurer dans les deux.
 *
 * ## Elle ne sait rien affecter, et c'est délibéré
 *
 * L'affectation reste le geste du plan de charge. Deux écrans qui écrivent la même chose, c'est
 * la panne du 16/08/2026 à l'échelle du produit — le chemin qui écrit doit être unique, sans quoi
 * l'un des deux finit par oublier une règle que l'autre applique. Cette fenêtre répond à une
 * question et une seule : *qui est libre, et quand ?*
 *
 * ## Les pannes, enfin (21/09/2026)
 *
 * Cet écran a porté pendant trois semaines un avertissement en clair : *« les pannes n'existent
 * nulle part »*. Il était vrai — le référentiel maître ne connaît aucune indisponibilité matériel,
 * et un ordre de réparation portait une gravité mais ni période ni solde (031). Le module SAV
 * (088 à 090) les fait naître : une remontée qualifiée « immobilisation » porte une période datée,
 * et les machines s'affichent immobilisées au même titre que les congés et les arrêts.
 *
 * **L'avertissement n'a pas disparu, il est devenu un compteur.** Zéro machine immobilisée et
 * « les immobilisations n'ont pas pu être lues » se ressemblent trop pour qu'on laisse le lecteur
 * trancher seul : c'est la règle du 16/08, celle-là même qui avait fait écrire l'avertissement.
 *
 * Le développement d'une période en jours se fait **ici**, et non au serveur qui rend des bornes :
 * le référentiel développe déjà les congés en jours ouvrés de son côté, et refaire ce calcul
 * ailleurs ferait deux calendriers là où le produit n'en veut qu'un (règle du 13/08). Une
 * immobilisation, elle, couvre **tous** les jours de sa période, week-end compris — une machine
 * chez le mécanicien n'est pas rendue le samedi.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import {
  ajouterJours,
  calendrierDesExceptions,
  estOuvre,
  formaterDateCourte,
  formaterDateLongue,
  formaterHeure,
  formaterNombre,
} from "@planif/moteur";
import {
  chargerCalendrier,
  chargerGeneriques,
  chargerImmobilisations,
  chargerIndisponibilites,
  chargerMateriels,
  chargerPlan,
  chargerSalaries,
} from "./api";
import type { ChantierAuPlan, JourException, Materiel, PlanGeneral, Salarie } from "./api";
import type { Generique } from "@planif/moteur";
import { PanneauPeriode } from "./Periode";
import type { Bornes } from "./Periode";
import { direLAbsence } from "./AbsencesAGerer";
import type { Absence } from "./AbsencesAGerer";
import { designerMateriel, designerSalarie } from "./designerRessource";
import {
  affecteesSurLeChantier,
  codesDuChantier,
  construirePlanning,
  apoDuMoment,
  direLOccupation,
  etatDuMoment,
  ressourcesDeLaVue,
  ressourcesDesMateriels,
  ressourcesDesSalaries,
} from "./planningParRessource";
import type { CelluleJour, LignePlanning, Moment, Occupation } from "./planningParRessource";
import { MESSAGE_ALLER_AU_PLAN } from "./cibles";
import type { CibleDuPlanDeCharge } from "./cibles";
import { couleursDesAffaires } from "./couleurDAffaire";
import { lignesDeMateriau, partParVehicule } from "./tonnageAchemine";
import type { LigneMateriau, PartDuVehicule } from "./tonnageAchemine";

/** Le nom de la fenêtre, partagé avec le bouton qui l'ouvre — un par vue. */
export function NOM_FENETRE(vue: Vue): string {
  /*
    **Un nom par vue, donc deux fenêtres possibles côte à côte.**

    Le responsable exploitation et le responsable transport ne sont pas la même personne, mais
    la même personne peut vouloir les deux sous les yeux — les chauffeurs d'un côté, les
    équipes de l'autre, le même jour. Un nom unique aurait fait que l'un écrase l'autre.
    À l'intérieur d'une vue, le nom reste fixe : rechanger d'affaire rafraîchit la fenêtre.
  */
  return `planif-planning-ressources-${vue}`;
}

/** Le chemin de la fenêtre. Reconnu par `App` avant tout affichage d'écran. */
export const CHEMIN_FENETRE = "/planning-ressources";

/** De quel côté de la case « transport » cette fenêtre regarde. */
export type Vue = "exploitation" | "transport";

/**
 * L'adresse à ouvrir, depuis le plan de charge.
 *
 * La période voyage dans l'adresse plutôt que d'être recalculée : les deux vues doivent regarder
 * le même horizon à l'ouverture, sinon on compare un planning de trois semaines à un plan de
 * charge de trois mois sans s'en apercevoir. La **vue** y voyage pour la même raison — c'est
 * l'onglet d'où l'on vient qui la décide, pas un réglage à refaire dans la fenêtre.
 */
export function adresseDuPlanning(bornes: {
  readonly debut: string;
  readonly fin: string;
  readonly chantierId: string;
  readonly vue: Vue;
}): string {
  const parametres = new URLSearchParams({ debut: bornes.debut, fin: bornes.fin });
  // Une affaire vide ne s'écrit pas : « toutes les affaires » est l'absence de filtre, et un
  // paramètre vide dans l'adresse se lirait comme un chantier dont l'identifiant a été perdu.
  if (bornes.chantierId !== "") parametres.set("affaire", bornes.chantierId);
  if (bornes.vue === "transport") parametres.set("vue", "transport");
  return `${CHEMIN_FENETRE}?${parametres.toString()}`;
}

/** Les jours ouvrés de l'horizon, dans l'ordre — mêmes règles que la frise du planning. */
function joursOuvres(
  debut: string,
  fin: string,
  exceptions: readonly JourException[],
): readonly string[] {
  const calendrier = calendrierDesExceptions(exceptions);
  const jours: string[] = [];
  let courant = debut;
  for (let pas = 0; pas < 400 && courant <= fin; pas += 1) {
    if (estOuvre(courant, calendrier)) jours.push(courant);
    courant = ajouterJours(courant, 1);
  }
  return jours;
}

/**
 * Une absence, en deux lettres.
 *
 * La case fait quatre caractères de large : le libellé complet y serait coupé, et « co… » ne dit
 * plus rien. L'infobulle porte la phrase entière, nature et validation comprises — la case ne
 * sert qu'à voir *qu'il y a quelque chose*, ce qui est exactement son rôle dans une grille de
 * cent trente lignes.
 */
function abregerAbsence(absence: Absence): string {
  if (absence.nature === "arret") return "AR";
  if (absence.nature === "conge") return "CG";
  return "AB";
}

/** Ce que raconte une demi-journée : l'absence d'abord, puis chaque occupation. */
function titreDuMoment(moment: Moment, absence: Absence | undefined, quand: string): string {
  const lignes: string[] = [quand];
  if (absence !== undefined) lignes.push(direLAbsence(absence));
  for (const occupation of moment.occupations) {
    lignes.push(direLOccupation(occupation, formaterHeure(occupation.heures)));
  }
  return lignes.join("\n");
}

function CelluleMoment({
  moment,
  absence,
  chantierId,
  quand,
  journee,
  aussiJournee,
  charge,
  couleurs,
  onRemonter,
}: {
  moment: Moment;
  absence: Absence | undefined;
  chantierId: string | null;
  quand: string;
  /** Vrai pour la case fusionnée des rotations et livraisons — un jour, pas une moitié. */
  journee?: boolean;
  /** Ce jour porte AUSSI une rotation ou une livraison, en plus des demi-journées. */
  aussiJournee?: Moment;
  /** La part du tonnage de l'ouvrage qui revient à ce véhicule, ce jour-là. */
  charge?: readonly PartDuVehicule[];
  /**
   * Remonter au plan de charge depuis cette case *(Chris, 17/09/2026)*.
   *
   * `undefined` quand la fenêtre n'a pas d'ouvreur : ouverte seule — un signet, un second
   * écran rallumé le lendemain —, elle n'a pas de plan de charge où renvoyer. La case reste
   * alors un nombre, plutôt qu'un bouton qui ne ferait rien.
   */
  onRemonter?: (occupations: readonly Occupation[]) => void;
  /**
   * La couleur de chaque affaire, quand on les regarde toutes *(Chris, 21/09/2026)*.
   *
   * Vide dès qu'une affaire est choisie : la case reprend alors le vert / orange / rouge de
   * `etatDuMoment`, qui répond à une autre question — *est-ce sur MON chantier ?* Teinter par
   * affaire dans ce cas-là ferait perdre la réponse qu'on venait chercher.
   */
  couleurs?: ReadonlyMap<string, string>;
}): JSX.Element {
  const etat = etatDuMoment(moment, chantierId);
  const classes = ["pr__case"];
  if (journee === true) classes.push("pr__case--journee");
  /*
    **La couleur de l'affaire, toutes affaires confondues.**

    Elle ne s'applique que là où `etatDuMoment` n'a rien à dire d'utile : sans affaire choisie,
    il rend « ailleurs » pour TOUT, donc un écran d'une seule teinte orange où l'on ne
    distinguait aucun chantier. C'est exactement ce que Chris a vu.

    Trois conditions, et chacune protège une réponse déjà donnée par cet écran : pas d'affaire
    choisie (sinon le vert de la sienne disparaîtrait), aucune absence (elle l'emporte sur tout),
    et une SEULE affaire dans la case — deux affaires le même moment gardent le motif du partage,
    qui est précisément ce qu'il faut voir.
  */
  const apo = chantierId === null ? apoDuMoment(moment) : null;
  const couleurDeLAffaire =
    apo === null || absence !== undefined ? undefined : couleurs?.get(apo);
  /*
    **Sans affaire choisie, « plusieurs affaires » se dit en ROUGE, pas en orange.**

    `etatDuMoment` rend « ailleurs » pour tout quand aucune affaire n'est regardée : une case
    partagée entre deux chantiers aurait donc pris l'orange, exactement comme une case qui n'en
    porte qu'un — et la légende aurait annoncé un rouge que rien n'affichait. Vu à l'écran le
    21/09/2026, où l'orange était en plus la couleur tirée par l'une des deux affaires.

    Le rouge du partage garde ici le sens qu'il a de l'autre côté : *deux chantiers réclament
    cette ressource au même moment*. C'est le seul cas où la case ne dit pas QUELLE affaire —
    parce qu'il y en a deux, et que c'est ça l'information.
  */
  const partageSansAffaire =
    chantierId === null && apo === null && moment.occupations.length > 0;
  // L'absence l'emporte sur l'occupation : une personne affectée un jour de congé est
  // précisément ce qu'il faut voir, et la couleur du chantier masquerait l'alerte.
  if (absence !== undefined) classes.push("pr__case--absent");
  else if (couleurDeLAffaire !== undefined) classes.push("pr__case--affaire");
  else if (partageSansAffaire) classes.push("pr__case--partage");
  else if (etat !== null) classes.push(`pr__case--${etat}`);
  if (aussiJournee !== undefined && aussiJournee.occupations.length > 0) {
    classes.push("pr__case--et-journee");
  }

  const aDesHeures = moment.occupations.some((o) => o.nature !== "livraison");
  const texte = aDesHeures
    ? formaterNombre(moment.heures, 1)
    : moment.occupations.length > 0
      ? "liv."
      : absence === undefined
        ? ""
        : abregerAbsence(absence);

  const titres = [titreDuMoment(moment, absence, quand)];
  if (aussiJournee !== undefined) {
    for (const occupation of aussiJournee.occupations) {
      titres.push(direLOccupation(occupation, formaterHeure(occupation.heures)));
    }
  }

  /*
    **Le tonnage n'est écrit que là où il y a des heures en face.** Une charge sans occupation
    serait une part calculée sur une rotation qui n'existe pas ce jour-là — le cas ne se produit
    pas, et l'écrire quand même ferait apparaître du tonnage sur une machine libre.
  */
  const aCharger =
    charge === undefined || charge.length === 0 || moment.occupations.length === 0
      ? null
      : charge.map((p) => `${formaterNombre(p.tonnes, 1)} ${p.unite}`).join(" · ");
  if (aCharger !== null) {
    titres.push(`À charger : ${aCharger} — la part de ce véhicule sur l'ouvrage`);
  }

  /*
    **Cliquer sur les heures ouvre le plan de charge sur cette saisie** — *demande de Chris,
    17/09/2026 : « comme cela à partir du planning on peut remonter à la source à modifier
    éventuellement directement ».*

    Le bouton emporte **aussi** les occupations de la journée (`aussiJournee`) quand il y en a :
    ce sont celles que l'infobulle de cette même case affiche déjà — une rotation qui partage le
    jour avec des demi-journées. Ne remonter que la moitié de ce qu'on lit dans la case aurait
    envoyé chercher le reste à la main.

    Un `<button>` et non un `onClick` sur la case : cette grille se parcourt au clavier autant
    qu'à la souris, et une cellule cliquable sans rôle n'est atteignable ni par tabulation ni par
    un lecteur d'écran.
  */
  const aRemonter =
    onRemonter === undefined
      ? null
      : [...moment.occupations, ...(aussiJournee?.occupations ?? [])];
  const cliquable = aRemonter !== null && aRemonter.length > 0 && texte !== "";
  if (cliquable) titres.push("Cliquer : ouvrir le plan de charge sur cette saisie.");

  return (
    <td
      className={classes.join(" ")}
      colSpan={journee === true ? 2 : undefined}
      style={couleurDeLAffaire === undefined ? undefined : { background: couleurDeLAffaire }}
      title={texte === "" && absence === undefined ? undefined : titres.join("\n")}
    >
      {cliquable && aRemonter !== null ? (
        <button
          type="button"
          className="pr__remonter"
          onClick={() => {
            onRemonter?.(aRemonter);
          }}
        >
          {texte}
        </button>
      ) : (
        texte
      )}
      {aCharger !== null && <span className="pr__charge">{aCharger}</span>}
    </td>
  );
}

/** Les deux moitiés d'un jour, ou la case fusionnée quand rien ne porte de moment. */
function CellulesDuJour({
  cellule,
  jour,
  chantierId,
  charge,
  couleurs,
  onRemonter,
}: {
  cellule: CelluleJour | undefined;
  jour: string;
  chantierId: string | null;
  /** Les couleurs par affaire, passées telles quelles aux cases. */
  couleurs?: ReadonlyMap<string, string>;
  /** Le jour est lié ici : les cases ne le portent pas, et le saut en a besoin. */
  onRemonter?: (occupations: readonly Occupation[], jour: string) => void;
  /**
   * Ce que ce véhicule charge ce jour-là — sa part du tonnage de l'ouvrage (16/09/2026).
   *
   * Affichée **sous** les heures et non à leur place : les heures restent ce qui décide si la
   * machine est prise, le tonnage est ce qui dit à quoi elle est prise.
   */
  charge?: readonly PartDuVehicule[];
}): JSX.Element {
  if (cellule === undefined) {
    /*
      Un véhicule qui charge un jour où il n'a aucune heure affectée n'arrive pas : la part se
      calcule à partir des rotations, qui sont justement ce qui pose les heures. La cellule vide
      reste donc vide.
    */
    return (
      <>
        <td className="pr__case" />
        <td className="pr__case" />
      </>
    );
  }

  const long = formaterDateLongue(jour);
  const remonter =
    onRemonter === undefined
      ? undefined
      : (occupations: readonly Occupation[]): void => {
          onRemonter(occupations, jour);
        };
  const riendeMoment =
    cellule.matin.occupations.length === 0 && cellule.apresMidi.occupations.length === 0;

  /*
    **Une rotation n'occupe pas deux demi-journées.** Quand un jour ne porte que des livraisons
    ou des rotations, la case est fusionnée : elles ont une heure limite, pas un matin, et les
    étaler sur les deux moitiés ferait lire « pris toute la journée » là où un camion passe à
    15 h. Une absence sans aucune occupation passe aussi par là — elle porte sur le jour, le
    référentiel ne la découpe pas.
  */
  if (riendeMoment) {
    return (
      <CelluleMoment
        moment={cellule.journee}
        absence={cellule.absence}
        chantierId={chantierId}
        couleurs={couleurs}
        quand={long}
        journee
        charge={charge}
        onRemonter={remonter}
      />
    );
  }

  /*
    La charge va sur la MOITIÉ DU MATIN, et non sur les deux : c'est une charge du jour, pas de
    chaque demi-journée. L'écrire deux fois la ferait lire comme un tonnage doublé — le même
    piège que les rotations étalées sur les deux moitiés, refermé le 03/09.
  */
  return (
    <>
      <CelluleMoment
        moment={cellule.matin}
        absence={cellule.absence}
        chantierId={chantierId}
        couleurs={couleurs}
        quand={`${long} — matin`}
        aussiJournee={cellule.journee}
        charge={charge}
        onRemonter={remonter}
      />
      <CelluleMoment
        moment={cellule.apresMidi}
        absence={cellule.absence}
        chantierId={chantierId}
        couleurs={couleurs}
        quand={`${long} — après-midi`}
        aussiJournee={cellule.journee}
        onRemonter={remonter}
      />
    </>
  );
}

/**
 * Les matériaux à acheminer, en tête du tableau du matériel.
 *
 * *Demande de Chris, 16/09/2026 : « idéalement tu fais apparaître la ligne juste au-dessus des
 * bennes […] tu gardes le même code couleur pour identifier l'APO. »*
 *
 * **Juste au-dessus des bennes, et non intercalées entre elles.** Une benne transporte plusieurs
 * matériaux sur la période, et plusieurs affaires : la ranger sous l'un d'eux la ferait
 * disparaître des autres, ou l'obligerait à figurer deux fois — et c'est cette liste qu'on
 * parcourt pour trouver une machine libre. Les matériaux forment donc un bloc au-dessus, et le
 * rattachement se lit dans la cellule : le tonnage de la ligne en haut, la part de chaque
 * véhicule dans sa propre case.
 *
 * Le code couleur est **exactement celui des autres cases** — vert l'affaire regardée, orange une
 * autre : c'est la même fonction `etatDuMoment` qui le décide, à partir du chantier du matériau.
 */
function LignesMateriau({
  materiaux,
  jours,
  chantierId,
}: {
  materiaux: readonly LigneMateriau[];
  jours: readonly string[];
  chantierId: string | null;
}): JSX.Element {
  return (
    <>
      {materiaux.map((materiau) => (
        <tr key={materiau.cle} className="pr__materiau">
          <th className="pr__entete-ressource">
            <strong>{materiau.code}</strong> {materiau.libelle}
            {/*
              **La provenance est nommée**, depuis que les achats « par nos soins » entrent dans
              la liste (16/09/2026) : une ligne de carrière et une ligne de fournisseur s'y
              ressemblent, et c'est la seule chose qui les distingue.
            */}
            <span className="note">
              {" "}
              · {materiau.provenance} · {materiau.apo} · poste {materiau.poste}
            </span>
            {/*
              **Court, parce que la colonne tronque.** Elle est `sticky` et bornée à 28 rem :
              « aucun véhicule affecté sur cet ouvrage » s'y coupait en plein milieu (vu à
              l'écran le 16/09/2026). La phrase entière passe en infobulle, comme partout
              ailleurs dans le produit.
            */}
            {materiau.sansVehicule && (
              <span
                className="en-ecart"
                title={
                  "Aucun véhicule du groupe n'est affecté à cet ouvrage sur la période affichée : " +
                  "ce tonnage n'a personne pour le charger."
                }
              >
                {" "}
                · rien pour le charger
              </span>
            )}
          </th>
          <td className="pr__heures">
            {formaterNombre(materiau.total, 1)} {materiau.unite}
          </td>
          {jours.map((jour) => {
            const quantite = materiau.jours.get(jour);
            const etat =
              quantite === undefined
                ? null
                : chantierId === null
                  ? "ailleurs"
                  : materiau.chantierId === chantierId
                    ? "ici"
                    : "ailleurs";
            return (
              <td
                key={jour}
                colSpan={2}
                className={`pr__case pr__case--journee${etat === null ? "" : ` pr__case--${etat}`}`}
                title={
                  quantite === undefined
                    ? undefined
                    : `${materiau.apo} · poste ${materiau.poste} ${materiau.posteLibelle}\n` +
                      `${formaterNombre(quantite, 1)} ${materiau.unite} à acheminer`
                }
              >
                {quantite === undefined
                  ? ""
                  : `${formaterNombre(quantite, 1)} ${materiau.unite}`}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function TableauPlanning({
  lignes,
  jours,
  chantierId,
  couleurs,
  avecAbsences,
  materiaux,
  charges,
  onRemonter,
}: {
  lignes: readonly LignePlanning[];
  jours: readonly string[];
  chantierId: string | null;
  /** La couleur de chaque affaire, quand on les regarde toutes. */
  couleurs?: ReadonlyMap<string, string>;
  /** Remonter au plan de charge depuis une case. Absent quand la fenêtre n'a pas d'ouvreur. */
  onRemonter?: (occupations: readonly Occupation[], jour: string) => void;
  /** La main d'œuvre porte une ligne de pied « absentes » ; le matériel n'en a pas la donnée. */
  avecAbsences: boolean;
  /** Les matériaux du groupe à acheminer — le tableau du matériel transport seulement. */
  materiaux?: readonly LigneMateriau[];
  /** Ce que chaque véhicule charge, indexé `ressourceId#date`. */
  charges?: ReadonlyMap<string, readonly PartDuVehicule[]>;
}): JSX.Element {
  /** Combien de ressources sont occupées, et combien absentes, chaque jour. */
  const pied = useMemo(() => {
    const parJour = new Map<string, { occupees: number; absentes: number }>();
    for (const jour of jours) parJour.set(jour, { occupees: 0, absentes: 0 });
    for (const ligne of lignes) {
      for (const [date, cellule] of ligne.jours) {
        const compte = parJour.get(date);
        if (compte === undefined) continue;
        if (cellule.absence !== undefined) compte.absentes += 1;
        if (
          cellule.matin.occupations.length > 0 ||
          cellule.apresMidi.occupations.length > 0 ||
          cellule.journee.occupations.length > 0
        ) {
          compte.occupees += 1;
        }
      }
    }
    return parJour;
  }, [lignes, jours]);

  return (
    <div className="plan__defilement">
      <table className="pr__grille">
        <thead>
          <tr>
            <th className="pr__entete-ressource" rowSpan={2}>
              Ressource
            </th>
            <th
              className="pr__heures"
              rowSpan={2}
              title="Heures affectées sur toute la période affichée, tous chantiers confondus"
            >
              Heures
            </th>
            {jours.map((jour) => (
              <th key={jour} className="plan__jour" colSpan={2}>
                {formaterDateCourte(jour)}
              </th>
            ))}
          </tr>
          <tr>
            {jours.map((jour) => (
              <Fragment key={jour}>
                <th className="pr__moment" title="matin">
                  M
                </th>
                <th className="pr__moment" title="après-midi">
                  A
                </th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {materiaux !== undefined && materiaux.length > 0 && (
            <LignesMateriau materiaux={materiaux} jours={jours} chantierId={chantierId} />
          )}
          {lignes.map((ligne) => (
            <tr key={ligne.ressourceId} className={ligne.duChantier ? "pr__du-chantier" : undefined}>
              <th className="pr__entete-ressource">
                <strong>{ligne.code ?? "—"}</strong> {ligne.libelle}
                {ligne.conflits > 0 && (
                  <span className="en-ecart">
                    {" "}
                    ·{" "}
                    {ligne.conflits === 1
                      ? "1 jour partagé"
                      : `${String(ligne.conflits)} jours partagés`}
                  </span>
                )}
              </th>
              <td className="pr__heures">
                {ligne.heures === "0" ? "" : formaterNombre(ligne.heures, 1)}
              </td>
              {jours.map((jour) => (
                <CellulesDuJour
                  key={jour}
                  cellule={ligne.jours.get(jour)}
                  jour={jour}
                  chantierId={chantierId}
                  couleurs={couleurs}
                  charge={charges?.get(`${ligne.ressourceId}#${jour}`)}
                  onRemonter={onRemonter}
                />
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="pr__entete-ressource">Occupées ce jour</th>
            <td className="pr__heures" />
            {jours.map((jour) => (
              <td key={jour} className="pr__case pr__case--pied" colSpan={2}>
                {pied.get(jour)?.occupees === 0 ? "" : String(pied.get(jour)?.occupees ?? 0)}
              </td>
            ))}
          </tr>
          {avecAbsences && (
            <tr>
              <th className="pr__entete-ressource">Absentes</th>
              <td className="pr__heures" />
              {jours.map((jour) => (
                <td key={jour} className="pr__case pr__case--pied" colSpan={2}>
                  {pied.get(jour)?.absentes === 0 ? "" : String(pied.get(jour)?.absentes ?? 0)}
                </td>
              ))}
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Ce que le transport retire de la liste, dit en une ligne.
 *
 * Rien du tout quand rien n'est retiré : une phrase « 0 ressource écartée » sous chaque tableau
 * serait du bruit permanent pour un fait qui n'arrive pas.
 */
function TransportEcarte({
  combien,
  quoi,
  cote,
}: {
  combien: number;
  quoi: string;
  /** « du transport » ou « hors transport » — de quel côté de la case sont les écartées. */
  cote: string;
}): JSX.Element | null {
  if (combien === 0) return null;
  return (
    <p className="note">
      {combien === 1 ? `1 ressource ${cote}` : `${String(combien)} ressources ${cote}`} ne{" "}
      {combien === 1 ? "figure" : "figurent"} pas ci-dessous ({quoi}) : c&apos;est la case
      « transport » du référentiel des ressources génériques qui les range de l&apos;autre côté, et
      la même case décide des deux vues. Les véhicules de moins de 3,5 t ne la portent pas — ils
      suivent les personnes, donc ils vivent avec l&apos;exploitation.
    </p>
  );
}

/** Ce que l'adresse demande : la période et l'affaire à regarder. */
function demandeDeLAdresse(): { bornes: Bornes | null; affaire: string; vue: Vue } {
  const parametres = new URLSearchParams(window.location.search);
  const debut = parametres.get("debut");
  const fin = parametres.get("fin");
  return {
    // Les deux bornes ou aucune : une seule laisserait le serveur compléter l'autre par son
    // défaut, et la fenêtre n'afficherait pas la période du plan de charge qui l'a ouverte.
    bornes: debut !== null && fin !== null ? { debut, fin } : null,
    affaire: parametres.get("affaire") ?? "",
    // L'exploitation par défaut, et une **liste fermée** : une valeur inattendue dans l'adresse
    // ne doit pas ouvrir une troisième vue qui n'existe pas.
    vue: parametres.get("vue") === "transport" ? "transport" : "exploitation",
  };
}

/** Comment chaque vue se nomme à l'écran. Un seul endroit, pour que titres et onglets s'accordent. */
const MOTS: Record<Vue, { titre: string; mo: string; materiel: string; ecartees: string }> = {
  exploitation: {
    titre: "Planning des ressources",
    mo: "Main d'œuvre",
    materiel: "Matériel",
    ecartees: "du transport",
  },
  transport: {
    titre: "Planning des ressources — transport",
    mo: "Main d'œuvre transport",
    materiel: "Matériel transport",
    ecartees: "hors transport",
  },
};

export function PlanningRessources(): JSX.Element {
  const demande = useMemo(demandeDeLAdresse, []);
  const [periode, setPeriode] = useState<Bornes | null>(demande.bornes);
  const [affaire, setAffaire] = useState<string>(demande.affaire);
  /** Faux = les ressources de l'affaire ; vrai = tout le référentiel. */
  const [tout, setTout] = useState(demande.affaire === "");
  const [recherche, setRecherche] = useState("");
  /**
   * Ne montrer que les ressources qui portent quelque chose sur la période.
   *
   * *Demande de Chris, 21/09/2026.* Faux à l'arrivée : l'écran répond « qui est libre, et
   * quand ? », et une liste dont les libres ont disparu ne répondrait plus qu'à la moitié de la
   * question. C'est un geste qu'on pose quand on veut lire le chargement, pas un état de départ.
   */
  const [utiliseesSeules, setUtiliseesSeules] = useState(false);

  const [plan, setPlan] = useState<PlanGeneral | null>(null);
  const [exceptions, setExceptions] = useState<readonly JourException[]>([]);
  const [salaries, setSalaries] = useState<readonly Salarie[]>([]);
  const [materiels, setMateriels] = useState<readonly Materiel[]>([]);
  /** Le référentiel des génériques : c'est lui qui dit ce qui est du transport. */
  const [generiques, setGeneriques] = useState<readonly Generique[]>([]);
  const [absences, setAbsences] = useState<ReadonlyMap<string, Absence>>(new Map());
  const [absencesSansSalarie, setAbsencesSansSalarie] = useState(0);
  /**
   * Les immobilisations machine, développées en jours — mêmes clés que les absences.
   *
   * Le développement se fait **ici et non au serveur** : celui-ci rend une période (début, remise
   * prévue), parce que le référentiel maître développe déjà les congés en jours ouvrés et que
   * refaire ce calcul de son côté ferait deux calendriers là où le produit n'en veut qu'un
   * (règle du 13/08). L'écran, lui, connaît déjà la fenêtre qu'il affiche.
   */
  const [immobilisations, setImmobilisations] = useState<ReadonlyMap<string, Absence>>(new Map());
  /** Combien de machines sont immobilisées : un compteur, pour que zéro se distingue de « on ne sait pas ». */
  const [machinesImmobilisees, setMachinesImmobilisees] = useState<number | null>(null);
  const [erreurReferentiel, setErreurReferentiel] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  /**
   * Le compteur d'actualisations *(Chris, 21/09/2026 : « pouvoir actualiser les informations
   * sans avoir à sortir puis rouvrir »)*.
   *
   * Un compteur plutôt qu'une fonction de rechargement appelée à la main : les trois lectures
   * de cet écran vivent dans trois effets aux dépendances différentes — le plan suit la période,
   * les référentiels ne bougeaient jamais, les absences suivent le plan. Les rejouer en les
   * appelant une à une aurait recopié cet enchaînement ailleurs, et la quatrième lecture qu'on
   * ajoutera un jour aurait été oubliée. Le compteur entre dans les dépendances : tout ce qui
   * lit se rejoue, dans le bon ordre, sans rien de nouveau à tenir.
   */
  const [rafraichissement, setRafraichissement] = useState(0);
  /** Quand la dernière lecture a abouti. Nul avant la première. */
  const [luA, setLuA] = useState<Date | null>(null);

  useEffect(() => {
    document.title = MOTS[demande.vue].titre;
  }, [demande.vue]);

  /**
   * La fenêtre qui nous a ouverts, si elle existe encore.
   *
   * Lue **une fois au montage** : `window.opener` est figé à l'ouverture, et le relire à chaque
   * rendu ne changerait rien sinon rendre la grille dépendante d'une valeur hors de React.
   * `null` quand la fenêtre a été ouverte seule — depuis un signet, ou rouverte par le
   * navigateur au démarrage : il n'y a alors aucun plan de charge où renvoyer.
   */
  const [ouvreur] = useState<Window | null>(() => window.opener as Window | null);

  /**
   * Ce que la dernière remontée a fait, écrit dans CETTE fenêtre.
   *
   * Parce que le résultat, lui, se produit dans l'autre — et que le navigateur ne la ramène pas
   * toujours devant. Voir `remonterAuPlan`.
   */
  const [remontee, setRemontee] = useState<string | null>(null);

  /**
   * Remonter au plan de charge — le premier maillon de la chaîne *(Chris, 17/09/2026)*.
   *
   * ## Elle ne navigue pas, elle **rend la main**
   *
   * Cette fenêtre a été ouverte à côté du plan de charge pour qu'on regarde les deux ensemble.
   * Y charger le plan de charge à la place du planning détruirait justement ce qu'elle sert à
   * faire — et on aurait deux plans de charge côte à côte, dont un qui écrit (la panne du
   * 16/08/2026, dont cet écran se garde déjà).
   *
   * Elle envoie donc la cible à sa fenêtre d'origine et lui donne le focus. Le geste se lit
   * comme « montre-moi ça là-bas », pas comme « emmène-moi ».
   *
   * ## L'origine est nommée aux deux bouts
   *
   * `postMessage` part avec l'origine exacte plutôt qu'avec `*`, et le receveur la revérifie :
   * un message qui porte des identifiants de chantier n'a pas à être lisible par une page tierce
   * qui aurait ouvert celle-ci.
   */
  const remonterAuPlan = useCallback(
    (occupations: readonly Occupation[], jour: string): void => {
      /*
        **La fenêtre d'origine a disparu : le dire, et ne pas échouer en silence** (18/09/2026).

        Elle peut être fermée, ou avoir navigué ailleurs, longtemps après l'ouverture de celle-ci.
        Les cases restaient cliquables — le test se faisait au montage — et le clic ne produisait
        alors **rien du tout**. Un geste qui ne fait rien et ne dit rien se lit comme une panne de
        la fonction, et c'est le seul diagnostic que l'utilisateur ne peut pas poser tout seul.
      */
      if (ouvreur === null || ouvreur.closed) {
        setRemontee(
          "La fenêtre du plan de charge n'est plus ouverte : il n'y a plus où renvoyer. " +
            "Rouvrez le planning des ressources depuis le plan de charge.",
        );
        return;
      }
      // Une seule affaire dans la case → on filtre dessus. Plusieurs → « toutes », sinon la
      // moitié de ce qu'on vient de cliquer serait masquée à l'arrivée.
      const affaires = new Set(occupations.map((o) => o.chantierId));
      const premier = occupations[0];
      const cible: CibleDuPlanDeCharge = {
        jour,
        vue: demande.vue,
        chantierId: affaires.size === 1 && premier !== undefined ? premier.chantierId : null,
        occupations: occupations.map((o) => ({
          chantierId: o.chantierId,
          ligneId: o.ligneId,
          poste: o.poste,
          rang: o.rang,
          nature: o.nature,
        })),
      };
      ouvreur.postMessage({ type: MESSAGE_ALLER_AU_PLAN, cible }, window.location.origin);
      /*
        **Ramener la fenêtre d'origine au premier plan n'est pas garanti**, et c'est le défaut du
        18/09/2026 : *« si je clique sur les heures d'une journée cela ne me redirige pas vers le
        planning ».*

        Les navigateurs refusent souvent `focus()` sur une autre fenêtre — la protection contre les
        fenêtres qui se mettent en avant toutes seules. Le message part, le plan de charge se
        positionne pour de bon, mais il le fait **derrière**, et rien ici ne bouge. Du point de vue
        de celui qui clique, le geste n'a servi à rien.

        On l'appelle quand même — quand il passe, c'est exactement ce qu'on veut — et on **écrit ce
        qui vient de se produire**, en nommant l'affaire et le poste. La phrase n'est pas un lot de
        consolation : c'est elle qui apprend que la réponse est dans l'autre fenêtre.

        *À noter : mon contrôle de la veille pilotait deux fenêtres sans notion de premier plan. Il
        a prouvé que le message arrive et que le plan de charge se positionne — jamais ce que
        l'utilisateur voit.*
      */
      ouvreur.focus();
      const ou = premier === undefined ? "" : ` sur ${premier.poste}`;
      setRemontee(
        `Plan de charge positionné${ou} au ${formaterDateCourte(jour)} — dans la fenêtre du plan de ` +
          "charge, derrière celle-ci.",
      );
    },
    [ouvreur, demande.vue],
  );

  useEffect(() => {
    let abandonne = false;
    setChargement(true);
    Promise.all([chargerPlan(periode ?? undefined), chargerCalendrier()])
      .then(([reponse, calendrier]) => {
        if (abandonne) return;
        setPlan(reponse.plan);
        setExceptions(calendrier.exceptions);
        setErreur(null);
        // L'heure ne se pose qu'en cas de SUCCÈS : après un échec, c'est encore la lecture
        // d'avant qui est à l'écran, et l'heure doit continuer de la dater. La rafraîchir
        // quand même ferait passer pour neuf ce qu'on n'a pas pu relire.
        setLuA(new Date());
      })
      .catch((x: unknown) => {
        if (!abandonne) setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        if (!abandonne) setChargement(false);
      });
    return () => {
      abandonne = true;
    };
  }, [periode, rafraichissement]);

  /*
    Les deux référentiels réels, chargés une fois. Leur panne est **affichée** ici, à la
    différence du plan de charge où elle ne prive que les listes de choix : sans eux, cette
    fenêtre n'a plus une seule ligne à montrer, et une grille vide se lirait « personne n'est
    occupé » — l'inverse de ce qu'il faut comprendre.
  */
  useEffect(() => {
    Promise.all([chargerSalaries(), chargerMateriels(), chargerGeneriques()])
      .then(([sal, mat, gen]) => {
        setSalaries(sal.salaries);
        setMateriels(mat.materiels);
        setGeneriques(gen.generiques);
        setErreurReferentiel(null);
      })
      .catch((x: unknown) => {
        setErreurReferentiel(x instanceof Error ? x.message : String(x));
      });
    // Les référentiels aussi : une machine sortie du parc ou un salarié embauché entre deux
    // lectures est exactement le genre de changement qu'on vient chercher en actualisant. Les
    // laisser sur `[]` aurait fait un bouton qui rafraîchit les trois quarts de l'écran.
  }, [rafraichissement]);

  useEffect(() => {
    if (plan === null) return;
    let abandonne = false;
    chargerIndisponibilites(plan.debut, plan.fin)
      .then((r) => {
        if (abandonne) return;
        setAbsences(
          new Map(
            r.indisponibilites.map((i) => [
              `${i.salarieId}#${i.date}`,
              { nature: i.nature, valide: i.valide },
            ]),
          ),
        );
        setAbsencesSansSalarie(r.sansSalarie);
      })
      .catch(() => {
        if (abandonne) return;
        setAbsences(new Map());
        setAbsencesSansSalarie(0);
      });

    /*
      Les immobilisations, développées sur la fenêtre affichée.

      Une immobilisation **couvre tous les jours de sa période**, week-end compris : une machine
      chez le mécanicien n'est pas rendue le samedi. C'est la différence avec un congé, que le
      référentiel développe en jours ouvrés — et c'est pourquoi le développement se fait ici, où
      l'on sait ce qu'on affiche, plutôt que dans une règle de calendrier de plus.
    */
    chargerImmobilisations()
      .then((r) => {
        if (abandonne) return;
        const carte = new Map<string, Absence>();
        for (const im of r.immobilisations) {
          if (im.debut === null || im.remisePrevue === null) continue;
          for (let jour = im.debut; jour <= im.remisePrevue; jour = ajouterJours(jour, 1)) {
            if (jour < plan.debut) continue;
            if (jour > plan.fin) break;
            carte.set(`${im.materielId}#${jour}`, { nature: "immobilisation", valide: true });
          }
        }
        setImmobilisations(carte);
        setMachinesImmobilisees(new Set(r.immobilisations.map((i) => i.materielId)).size);
      })
      .catch(() => {
        if (abandonne) return;
        setImmobilisations(new Map());
        // `null` et non 0 : « on n'a pas pu savoir » ne se confond pas avec « aucune panne ».
        setMachinesImmobilisees(null);
      });
    return () => {
      abandonne = true;
    };
  }, [plan]);

  const jours = useMemo(
    () => (plan === null ? [] : joursOuvres(plan.debut, plan.fin, exceptions)),
    [plan, exceptions],
  );

  const besoins = plan?.besoins ?? [];
  const chantiers: readonly ChantierAuPlan[] = plan?.chantiers ?? [];
  const chantierRegarde = affaire === "" ? null : affaire;

  /*
    Les codes du chantier se calculent dès qu'une affaire est choisie, **même quand on affiche
    tout** : ils ne servent pas qu'à filtrer, ils marquent les candidats. C'est ce qui permet de
    dérouler les 227 machines du parc en gardant sous les yeux celles qui relèvent de l'affaire —
    sans quoi « tout afficher » ferait perdre le repère qu'on venait justement de se donner.
  */
  const codesRetenus = useMemo(
    () => (chantierRegarde === null ? null : codesDuChantier(besoins, chantierRegarde)),
    [besoins, chantierRegarde],
  );
  const affectees = useMemo(
    () =>
      chantierRegarde === null
        ? new Set<string>()
        : affecteesSurLeChantier(besoins, chantierRegarde),
    [besoins, chantierRegarde],
  );

  /*
    **Ce qu'on achemine, et ce que chaque véhicule en charge** (Chris, 16/09/2026).

    Calculés sur TOUS les besoins, jamais sur ceux de l'affaire regardée : un camion partagé entre
    deux chantiers charge pour les deux, et n'en montrer qu'un ferait lire une demi-charge comme
    une charge complète. C'est la même règle que le reste de l'écran — on filtre l'affichage, pas
    le calcul.
  */
  const apoParChantier = useMemo(
    () => new Map(chantiers.map((c) => [c.chantierId, c.apo])),
    [chantiers],
  );

  /**
   * Une couleur par affaire *(Chris, 21/09/2026)*.
   *
   * Déduite de l'APO, donc stable d'un jour à l'autre et d'une période à l'autre : voir
   * `couleurDAffaire.ts`. Calculée pour TOUTES les affaires du plan, même quand une seule est
   * regardée — la légende doit pouvoir la nommer sans que la carte se reconstruise au moment
   * où l'on bascule.
   */
  const couleursParAffaire = useMemo(
    () => couleursDesAffaires(chantiers.map((c) => c.apo)),
    [chantiers],
  );
  const materiaux = useMemo(
    () => lignesDeMateriau(besoins, apoParChantier),
    [besoins, apoParChantier],
  );
  const charges = useMemo(() => partParVehicule(besoins), [besoins]);

  /**
   * Les deux plannings.
   *
   * Le filtre est appliqué **après** la construction et non avant : le planning d'une ressource
   * doit continuer de porter ses heures des autres chantiers, sinon regarder une affaire ferait
   * disparaître la raison même pour laquelle la personne n'est pas libre. C'est la règle que le
   * plan de charge tient déjà sur son filtre par affaire.
   */
  /**
   * Les codes que le référentiel déclare « transport ».
   *
   * *Demande de Chris, 03/09/2026 : ni les chauffeurs, ni les camions et remorques — mais les
   * véhicules de moins de 3,5 t restent, « ils sont à affecter avec les personnels qui viennent
   * sur chantier ».*
   *
   * Cette liste vient de la case du référentiel (019) et de nulle part ailleurs : les VU, VUL et
   * VP ne sont pas cochés précisément parce qu'ils suivent les personnes, et aucune règle tirée
   * du code ou du libellé n'aurait su les distinguer d'un camion.
   */
  const codesDeTransport = useMemo(
    () => new Set(generiques.filter((g) => g.estTransport).map((g) => g.code)),
    [generiques],
  );

  const moDuReferentiel = useMemo(
    () =>
      ressourcesDeLaVue(
        ressourcesDesSalaries(salaries, designerSalarie),
        codesDeTransport,
        demande.vue,
      ),
    [salaries, codesDeTransport, demande.vue],
  );
  const parcDuReferentiel = useMemo(
    () =>
      ressourcesDeLaVue(
        ressourcesDesMateriels(materiels, designerMateriel),
        codesDeTransport,
        demande.vue,
      ),
    [materiels, codesDeTransport, demande.vue],
  );

  const planningMo = useMemo(
    () =>
      construirePlanning({
        ressources: moDuReferentiel.retenues,
        besoins,
        chantiers,
        absences,
        chantierId: chantierRegarde,
        codesRetenus,
        affectees,
      }),
    [moDuReferentiel, besoins, chantiers, absences, chantierRegarde, codesRetenus, affectees],
  );

  const planningMateriel = useMemo(
    () =>
      construirePlanning({
        ressources: parcDuReferentiel.retenues,
        besoins,
        chantiers,
        // Les immobilisations du SAV (090), depuis le 21/09/2026. C'est ce que cet écran
        // annonçait ne pas savoir dire depuis le 03/09.
        absences: immobilisations,
        chantierId: chantierRegarde,
        codesRetenus,
        affectees,
      }),
    [parcDuReferentiel, besoins, chantiers, immobilisations, chantierRegarde, codesRetenus, affectees],
  );

  /**
   * Les lignes à montrer, dans l'ordre où les filtres se composent.
   *
   * **Le filtre « utilisées » s'applique en DERNIER et sur le résultat, jamais sur le calcul**
   * *(Chris, 21/09/2026 : « pouvoir filtrer uniquement sur les ressources utilisées pour
   * restreindre les lignes »)*. C'est la règle que cet écran tient partout : le planning d'une
   * ressource garde ses heures des autres chantiers, on cache des lignes, on ne change aucun
   * chiffre. Le pied du tableau continue donc de compter sur ce qui est affiché.
   *
   * « Utilisée » se mesure sur les OCCUPATIONS et non sur les heures : une benne qui ne fait que
   * des rotations porte zéro heure et n'en est pas moins prise tous les jours.
   */
  const filtrer = (lignes: readonly LignePlanning[]): readonly LignePlanning[] => {
    const cherche = recherche.trim().toLocaleLowerCase("fr");
    let retenues = tout || codesRetenus === null ? lignes : lignes.filter((l) => l.duChantier);
    if (utiliseesSeules) retenues = retenues.filter((l) => l.occupations > 0);
    if (cherche === "") return retenues;
    return retenues.filter(
      (l) =>
        l.libelle.toLocaleLowerCase("fr").includes(cherche) ||
        (l.code ?? "").toLocaleLowerCase("fr").includes(cherche),
    );
  };

  const moAffichees = filtrer(planningMo);
  const materielsAffiches = filtrer(planningMateriel);
  /**
   * Combien de ressources portent quelque chose, dans le périmètre d'affiche courant.
   *
   * Compté **avant** la coche et après le choix d'affiche : c'est ce que cocher laisserait, ce
   * qui est la seule chose utile à écrire sur une case à cocher. Compté après, il vaudrait le
   * nombre de lignes déjà à l'écran et n'apprendrait rien.
   */
  const combienUtilisees =
    [...planningMo, ...planningMateriel].filter(
      (l) => (tout || codesRetenus === null || l.duChantier) && l.occupations > 0,
    ).length;
  const nomDeLAffaire = chantiers.find((c) => c.chantierId === affaire);

  return (
    <main className="pr">
      <header className="entete">
        <h1>{MOTS[demande.vue].titre}</h1>
      </header>

      <p className="etat">
        Ce que chaque ressource a déjà sur les bras, jour par jour et demi-journée par
        demi-journée, <strong>tous chantiers confondus</strong>. Lecture seule : l&apos;affectation
        se fait dans le plan de charge, qui reste ouvert à côté.
      </p>

      <div className="pr__commandes">
        <label className="plan__filtre">
          Affaire
          <select
            value={affaire}
            onChange={(e) => {
              setAffaire(e.target.value);
              // Choisir une affaire rétablit le filtre : c'est le geste qui dit « je prépare
              // celle-là », et rester sur les 361 ressources du référentiel obligerait à
              // recliquer chaque fois.
              setTout(e.target.value === "");
            }}
          >
            <option value="">toutes les affaires ({String(chantiers.length)})</option>
            {chantiers.map((c) => (
              <option key={c.chantierId} value={c.chantierId}>
                {c.apo} — {c.nom}
              </option>
            ))}
          </select>
        </label>

        <div className="pr__bascule">
          <button
            type="button"
            className={tout ? undefined : "actif"}
            disabled={affaire === ""}
            onClick={() => {
              setTout(false);
            }}
          >
            {nomDeLAffaire === undefined
              ? "Ressources de l'affaire"
              : `Ressources de ${nomDeLAffaire.apo}`}
          </button>
          <button
            type="button"
            className={tout ? "actif" : undefined}
            onClick={() => {
              setTout(true);
            }}
          >
            Toutes les ressources (
            {String(moDuReferentiel.retenues.length + parcDuReferentiel.retenues.length)})
          </button>
        </div>

        {/*
          **« Utilisées seulement », demandé par Chris le 21/09/2026** pour restreindre les
          lignes. Une case à cocher et non un troisième bouton de la bascule : elle se COMBINE
          avec le choix d'affiche — « ressources de l'affaire » ou « toutes » — au lieu de le
          remplacer. Sur le parc entier, c'est ce qui fait passer de 227 machines à celles qui
          sont réellement prises sur la période.

          Le compte est écrit dessus : coché ou non, il dit combien de lignes le filtre laisse.
          Sans lui, une coche qui ne retirerait rien se lirait comme une coche qui ne marche pas.
        */}
        <label className="plan__filtre pr__utilisees">
          <input
            type="checkbox"
            checked={utiliseesSeules}
            onChange={(e) => {
              setUtiliseesSeules(e.target.checked);
            }}
          />
          Utilisées seulement ({String(combienUtilisees)})
        </label>

        <label className="plan__filtre">
          Chercher
          <input
            type="search"
            className="saisie"
            value={recherche}
            placeholder="nom, code, immatriculation…"
            onChange={(e) => {
              setRecherche(e.target.value);
            }}
          />
        </label>
      </div>

      <PanneauPeriode
        affichee={plan === null ? null : { debut: plan.debut, fin: plan.fin }}
        chargement={chargement}
        onBorner={setPeriode}
        onDefaut={() => {
          setPeriode(null);
        }}
        actualiseA={luA}
        onActualiser={() => {
          setRafraichissement((n) => n + 1);
        }}
      />

      {/*
        **Deux légendes, parce qu'il y a deux codes couleur** — et jamais les deux en même temps.

        Sans affaire choisie, la couleur dit QUELLE affaire ; avec une affaire choisie, elle dit
        si c'est la sienne. Afficher les deux légendes côte à côte laisserait croire qu'un vert
        et un bleu coexistent dans le même tableau, alors que l'un remplace l'autre.
      */}
      {chantierRegarde === null ? (
        <p className="pr__legende">
          <strong>Une couleur par affaire :</strong>
          {chantiers.map((c) => (
            <span key={c.chantierId}>
              <span
                className="pr__case"
                style={{ background: couleursParAffaire.get(c.apo) }}
                title={`${c.apo} — ${c.nom}`}
              >
                &nbsp;&nbsp;
              </span>{" "}
              {c.apo}
            </span>
          ))}
          <span className="pr__case pr__case--partage">&nbsp;&nbsp;</span> plusieurs affaires le
          même moment
          <span className="pr__case pr__case--journee">&nbsp;&nbsp;</span> rotation ou livraison
          (un jour, pas une demi-journée)
          <span className="pr__case pr__case--absent">&nbsp;&nbsp;</span> congé, arrêt ou
          immobilisation
        </p>
      ) : (
        <p className="pr__legende">
          <span className="pr__case pr__case--ici">&nbsp;&nbsp;</span> sur l&apos;affaire regardée
          <span className="pr__case pr__case--ailleurs">&nbsp;&nbsp;</span> sur un autre chantier
          <span className="pr__case pr__case--partage">&nbsp;&nbsp;</span> les deux le même jour
          <span className="pr__case pr__case--journee">&nbsp;&nbsp;</span> rotation ou livraison
          (un jour, pas une demi-journée)
          <span className="pr__case pr__case--absent">&nbsp;&nbsp;</span> congé ou arrêt
        </p>
      )}

      {/*
        Ce que la dernière remontée a fait. `role="status"` et non `alert` : c'est une confirmation,
        pas un problème — et un lecteur d'écran doit l'annoncer sans interrompre la lecture en cours.
      */}
      {remontee !== null && (
        <p className="pr__remontee" role="status">
          {remontee}
        </p>
      )}

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {erreurReferentiel !== null && (
        <p className="alerte" role="alert">
          Le référentiel maître n&apos;a pas répondu : {erreurReferentiel} Sans lui, aucune
          ressource ne peut être nommée — cette fenêtre reste vide, ce qui ne veut pas dire que
          personne n&apos;est occupé.
        </p>
      )}

      {absencesSansSalarie > 0 && (
        <p className="alerte">
          {absencesSansSalarie === 1
            ? "1 absence du référentiel n'est rattachée à aucune fiche salarié"
            : `${String(absencesSansSalarie)} absences du référentiel ne sont rattachées à aucune fiche salarié`}{" "}
          sur cette période : elles n&apos;apparaissent nulle part ci-dessous, et les personnes
          concernées se lisent comme <strong>disponibles</strong>. À corriger dans le référentiel
          des salariés, pas ici.
        </p>
      )}

      {chargement && <p className="etat">Chargement du planning…</p>}

      <h2>{MOTS[demande.vue].mo}</h2>
      {/*
        **Ce qui est retiré est compté, et la case qui le décide est nommée.**

        Le transport ne s'affiche pas ici (demande de Chris, 03/09/2026), mais une liste amputée
        se lit exactement comme une liste complète. Le compte sert aussi de diagnostic : s'il
        vaut zéro alors qu'on attend des chauffeurs en moins, c'est que la case « transport » du
        référentiel n'est pas cochée sur ces codes — et on cherche au bon endroit du premier coup.
      */}
      <TransportEcarte
        combien={moDuReferentiel.ecartees}
        cote={MOTS[demande.vue].ecartees}
        quoi={demande.vue === "transport" ? "équipes et encadrement" : "chauffeurs et codes de transport"}
      />
      {moAffichees.length === 0 ? (
        <p className="etat">
          {/*
            **La recherche est dite AVANT le reste, et c'est un défaut mesuré au navigateur le
            03/09/2026** : chercher « PELLE » vidait le tableau de la main d'œuvre, qui annonçait
            « aucun salarié au référentiel » — faux, il y en a cent trente-six, et la cause était
            le mot qu'on venait de taper. Un écran qui accuse le référentiel de ce que l'on vient
            de faire soi-même envoie chercher la panne au mauvais endroit.
          */}
          {recherche.trim() !== ""
            ? `Aucun salarié ne répond à « ${recherche.trim()} ».`
            : tout || codesRetenus === null
              ? "Aucun salarié au référentiel."
              : "Aucun salarié du référentiel n'est rattaché aux codes de main d'œuvre de cette affaire. Affichez toutes les ressources pour chercher."}
        </p>
      ) : (
        <TableauPlanning
          lignes={moAffichees}
          jours={jours}
          chantierId={chantierRegarde}
          couleurs={couleursParAffaire}
          avecAbsences
          onRemonter={ouvreur === null ? undefined : remonterAuPlan}
        />
      )}

      <h2>{MOTS[demande.vue].materiel}</h2>
      {/*
        **Dit avant le tableau, et pas en note de bas de page.** La demande portait sur « les
        pannes et les heures déjà affectées » : la seconde moitié est là, la première n'existe
        nulle part. Un tableau qui n'affiche que des heures se lit « aucune machine en panne »,
        et c'est le pire mode de panne d'un écran de contrôle (règle du 16/08/2026).
      */}
      <TransportEcarte
        combien={parcDuReferentiel.ecartees}
        cote={MOTS[demande.vue].ecartees}
        quoi={
          demande.vue === "transport"
            ? "engins de chantier et véhicules de moins de 3,5 t"
            : "tracteurs, camions, bennes et remorques"
        }
      />
      {/*
        L'avertissement du 03/09 disparaît : les pannes existent (090).

        Ce qui le remplace est un **compteur**, et non un silence. Zéro machine immobilisée et
        « on n'a pas pu lire les immobilisations » se ressemblent trop pour qu'on laisse le
        lecteur trancher seul — c'est la règle du 16/08, et c'est elle qui avait fait écrire
        l'avertissement précédent.
      */}
      <p className="etat">
        {machinesImmobilisees === null ? (
          <strong>
            Les immobilisations n&apos;ont pas pu être lues : ce tableau peut montrer disponible
            une machine qui ne l&apos;est pas.
          </strong>
        ) : machinesImmobilisees === 0 ? (
          "Aucune machine n'est immobilisée par le SAV aujourd'hui."
        ) : (
          <>
            <strong>
              {machinesImmobilisees === 1
                ? "1 machine est immobilisée"
                : `${String(machinesImmobilisees)} machines sont immobilisées`}
            </strong>{" "}
            par le SAV, jusqu&apos;à leur remise à disposition prévue.
          </>
        )}{" "}
        Les machines sorties du parc, elles, ne figurent pas dans cette liste.
      </p>
      {materielsAffiches.length === 0 ? (
        <p className="etat">
          {recherche.trim() !== ""
            ? `Aucune machine ne répond à « ${recherche.trim()} ».`
            : tout || codesRetenus === null
              ? "Aucune machine au parc."
              : "Aucune machine du parc n'est rattachée aux codes matériel de cette affaire. Affichez toutes les ressources pour chercher."}
        </p>
      ) : (
        <TableauPlanning
          lignes={materielsAffiches}
          jours={jours}
          chantierId={chantierRegarde}
          couleurs={couleursParAffaire}
          avecAbsences={false}
          /*
            **La vue transport seulement.** L'exploitation affecte des hommes et des machines à un
            travail ; ce qu'il y a dans la benne ne la regarde pas, et ses véhicules de moins de
            3,5 t ne transportent aucun matériau. Une affaire choisie ne restreint pas la liste :
            on cherche justement à voir ce que le parc a déjà sur les bras.
          */
          materiaux={demande.vue === "transport" ? materiaux : undefined}
          charges={demande.vue === "transport" ? charges : undefined}
          onRemonter={ouvreur === null ? undefined : remonterAuPlan}
        />
      )}
    </main>
  );
}
